import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link2, RefreshCw } from 'lucide-react';
import ConsumablePicker, { money } from './ConsumablePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tool-crib`;
// first real word of a tool-list label ("Zip cuts — 5\" x 3/64\"" → "Zip") seeds the consumables search
export const firstWord = (name) => ((name || '').match(/[A-Za-z]{3,}/) || [''])[0];

// "price needed" / link control for one costed consumable line on a closed kit
export function EntryPriceBox({ kit, entry, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const pick = async (choice) => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/kits/${kit.id}/entries/${entry.entry_id}/price`, choice.consumable_ref ? { consumable_ref: choice.consumable_ref } : { unit_cost: choice.unit_cost });
      onChanged(r.data.kit);
      toast.success(`${entry.name}: ${money(r.data.entry.unit_cost)} each${choice.consumable_ref ? ` — linked to ${choice.name}` : ''}${r.data.saved_to_tool_list ? ' · remembered on the tool list' : ''}`);
      setOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not set price'); }
    setBusy(false);
  };
  if (open) return <ConsumablePicker onPick={pick} onClose={() => setOpen(false)} initialQuery={firstWord(entry.name)} busy={busy} testId={`kit-entry-picker-${entry.item_id}`} />;
  return (
    <button type="button" onClick={() => setOpen(true)} className={`h-8 px-2.5 rounded-lg text-xs font-bold inline-flex items-center gap-1 ${entry.needs_price ? 'bg-amber-600 text-white' : 'bg-slate-100 text-slate-700'}`} data-testid={`kit-entry-link-${entry.item_id}`}>
      <Link2 className="w-3.5 h-3.5" /> {entry.needs_price ? 'Link to consumable / set price' : 'Change'}
    </button>
  );
}

export function RepriceButton({ kit, onChanged }) {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/kits/${kit.id}/reprice`);
      onChanged(r.data.kit);
      const bits = [];
      if (r.data.converted.length) bits.push(`${r.data.converted.length} moved from Missing tools to consumables`);
      if (r.data.repriced.length) bits.push(`${r.data.repriced.length} priced from the catalog`);
      if (r.data.still_unpriced.length) bits.push(`${r.data.still_unpriced.length} still need a price`);
      toast[bits.length && !r.data.converted.length && !r.data.repriced.length ? 'info' : 'success'](bits.length ? bits.join(' · ') : 'Nothing to re-price — everything is already costed');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not re-price'); }
    setBusy(false);
  };
  return (
    <button type="button" onClick={go} disabled={busy} className="h-8 px-2.5 rounded-lg bg-blue-600 text-white text-xs font-bold inline-flex items-center gap-1 disabled:opacity-60" title="Pull prices from the tool list / consumables module and move used-up consumables out of Missing tools" data-testid="kit-reprice">
      <RefreshCw className={`w-3.5 h-3.5 ${busy ? 'animate-spin' : ''}`} /> Re-price from catalog
    </button>
  );
}
